import { Suspense, type ReactNode } from 'react'
import { useLocation, Navigate, matchRoutes } from 'react-router-dom'
import { ToastContainer, type ToastContainerProps } from 'react-toastify'
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import 'react-toastify/dist/ReactToastify.css'

import { AppShell } from '@/components/layout/AppShell'
import { PageLoader } from '@/components/common/Loader'
import { RoutesMapper, filterRoutes, getDrawerItems } from '@/utils/routes'
import { useMaintenance } from '@/lib/maintenance'
import { getSessionToken, getRoles } from '@/lib/auth'
import { ROUTES } from '@/constants/routes'
import LoginPage from '@/pages/auth/LoginPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import NotFoundPage from '@/pages/status/NotFoundPage'
import MaintenancePage from '@/pages/status/MaintenancePage'

const TOAST_ICONS: Record<string, ReactNode> = {
  success: <CheckCircle2 className="size-4 text-success" aria-hidden="true" />,
  warning: <AlertTriangle className="size-4 text-warning" aria-hidden="true" />,
  error: <AlertCircle className="size-4 text-error" aria-hidden="true" />,
  info: <Info className="size-4 text-primary" aria-hidden="true" />,
}

/**
 * Ledger toast theme: flat surface, 4px tone rule on the left, 16px outline icon.
 * Colors come from `.ff-toast--<type>` in index.css; success/info auto-dismiss, errors persist
 * (see showToastMessage in utils).
 */
const TOAST_PROPS: ToastContainerProps = {
  position: 'bottom-right',
  limit: 3,
  autoClose: 5000,
  hideProgressBar: true,
  newestOnTop: true,
  closeOnClick: false,
  draggable: false,
  theme: 'light',
  icon: ({ type }) => TOAST_ICONS[type] ?? TOAST_ICONS.info,
  toastClassName: (context) => `ff-toast ff-toast--${context?.type ?? 'info'}`,
}

export default function App() {
  const location = useLocation()
  const token = getSessionToken()
  const roles = getRoles()
  const maintenance = useMaintenance()

  // ── Maintenance — env flag or API 503; takes over every screen, login included ──
  if (maintenance) return <MaintenancePage />

  // ── Login route — no AppShell, no auth required ───────────────────────────
  if (location.pathname === '/login' || location.pathname === '/auth/login') {
    // Already authenticated → skip login screen
    if (token) return <Navigate to="/" replace />
    return <LoginPage />
  }

  if (location.pathname === '/reset-password') {
    if (!token) return <Navigate to="/login" replace />
    return <ResetPasswordPage />
  }

  // ── Auth guard — redirect unauthenticated users to login ──────────────────
  if (!token) {
    return <Navigate to="/login" replace />
  }

  // ── Main app ──────────────────────────────────────────────────────────────
  const navItems = getDrawerItems(ROUTES, roles)
  const homePath = roles.includes('ADMIN') ? '/' : '/challans'

  // ── Catch-all — paths this user has no route for get the 404 page ─────────
  if (!matchRoutes(filterRoutes(ROUTES, roles).map(({ path }) => ({ path })), location)) {
    return <NotFoundPage homePath={homePath} />
  }

  return (
    <AppShell title="Operations" navItems={navItems}>
      <ToastContainer {...TOAST_PROPS} />
      <Suspense fallback={<PageLoader />}>
        <RoutesMapper routes={ROUTES} roles={roles} />
      </Suspense>
    </AppShell>
  )
}
